<template>
  <div class="flex flex-wrap gap-2 mb-6 p-6">
    <button
      v-for="tab in tabs"
      :key="tab"
      @click="setActiveTab(tab)"
      :disabled="isLoading"
      :class="[
        'px-4 py-2 rounded-lg font-medium transition-colors',
        activeTab === tab
          ? 'bg-blue-600 text-white'
          : 'bg-gray-200 text-gray-600 hover:bg-gray-300',
      ]"
    >
      {{ tab }}
    </button>
  </div>

  <div class="flex items-center gap-3 mb-4 px-6">
    <i class="fa-solid fa-layer-group text-blue-600"></i>
    <h1 class="text-xl font-semibold text-gray-700">
      Detalhamento por {{ activeTab }}
    </h1>
  </div>

  <section class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 p-6">
    <div
      v-for="(chart, index) in charts"
      :key="index"
      class="bg-white rounded-2xl shadow-lg p-6 flex flex-col items-center"
    >
      <h2 class="text-center font-semibold mb-4 text-gray-700">
        {{ chart.title }}
      </h2>
      <DoughnutChart :data="chart.data" :options="chartOptions" />
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue'
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from 'chart.js'
import { Doughnut } from 'vue-chartjs'
import { debounce } from 'lodash-es'

import { getSIDomainsByIdEnterprise } from '@/services/graphs/api.domains_si.js'
import { getTypes_ControlByIdEnterprise } from '@/services/graphs/api.types_control.js'
import { getCybersecurityConceptsByIdEnterprise } from '@/services/graphs/api.cybersecurity_concept.js'

ChartJS.register(Title, Tooltip, Legend, ArcElement)
const DoughnutChart = Doughnut

const props = defineProps({
  enterpriseId: { type: String, required: true },
})

const tabs = ['Domínios', 'Tipos de Controle', 'Conceitos de Cibersegurança']
const activeTab = ref('Domínios')
const isLoading = ref(false)
const charts = reactive([])

const chartOptions = {
  responsive: true,
  cutout: '70%',
  plugins: {
    legend: { position: 'bottom', labels: { usePointStyle: true } },
  },
}

// Para cancelar requisições pendentes
let lastRequestToken = null

function createDataset(implemented, partial, notImplemented) {
  return {
    labels: ['Implementado', 'Parcial', 'Não Implementado'],
    datasets: [
      {
        data: [implemented || 0, partial || 0, notImplemented || 0],
        backgroundColor: ['#22c55e', '#facc15', '#ef4444'],
        borderWidth: 1,
      },
    ],
  }
}

function getZeroCharts(tab) {
  if (tab === 'Domínios') {
    return [
      { title: 'Governança e Ecossistema', data: createDataset(0, 0, 0) },
      { title: 'Proteção', data: createDataset(0, 0, 0) },
      { title: 'Defesa', data: createDataset(0, 0, 0) },
      { title: 'Resiliência', data: createDataset(0, 0, 0) },
    ]
  } else if (tab === 'Tipos de Controle') {
    return [
      { title: 'Corretivo', data: createDataset(0, 0, 0) },
      { title: 'Detectivo', data: createDataset(0, 0, 0) },
      { title: 'Preventivo', data: createDataset(0, 0, 0) },
    ]
  } else if (tab === 'Conceitos de Cibersegurança') {
    return [
      { title: 'Detectar', data: createDataset(0, 0, 0) },
      { title: 'Identificar', data: createDataset(0, 0, 0) },
      { title: 'Proteger', data: createDataset(0, 0, 0) },
      { title: 'Recuperar', data: createDataset(0, 0, 0) },
      { title: 'Responder', data: createDataset(0, 0, 0) },
    ]
  }
  return []
}

function createChart(title, implemented, partial, notImplemented) {
  return {
    title,
    data: createDataset(implemented, partial, notImplemented),
  }
}

// Função principal com cancelamento de requisições
async function loadCharts(id, requestToken) {
  charts.length = 0
  if (!id) return

  isLoading.value = true
  try {
    let data = []

    if (activeTab.value === 'Domínios')
      data = await getSIDomainsByIdEnterprise(id)
    else if (activeTab.value === 'Tipos de Controle')
      data = await getTypes_ControlByIdEnterprise(id)
    else if (activeTab.value === 'Conceitos de Cibersegurança')
      data = await getCybersecurityConceptsByIdEnterprise(id)

    if (lastRequestToken !== requestToken) return

    if (data.length > 0) {
      const d = Array.isArray(data) ? data[0] : data

      if (activeTab.value === 'Domínios') {
        charts.push(
          createChart(
            'Governança e Ecossistema',
            d.DomainsGovernanceEcosystemImplemented,
            d.DomainsGovernanceEcosystemPartiallyImplemented,
            d.DomainsGovernanceEcosystemNotImplemented
          ),
          createChart(
            'Proteção',
            d.DomainsProtectionImplemented,
            d.DomainsProtectionPartiallyImplemented,
            d.DomainsProtectionNotImplemented
          ),
          createChart(
            'Defesa',
            d.DomainsDefenseImplemented,
            d.DomainsDefensePartiallyImplemented,
            d.DomainsDefenseNotImplemented
          ),
          createChart(
            'Resiliência',
            d.DomainsResilienceImplemented,
            d.DomainsResiliencePartiallyImplemented,
            d.DomainsResilienceNotImplemented
          )
        )
      } else if (activeTab.value === 'Tipos de Controle') {
        charts.push(
          createChart(
            'Corretivo',
            d.TypeControlCorrectiveImplemented,
            d.TypeControlCorrectivePartiallyImplemented,
            d.TypeControlCorrectiveNotImplemented
          ),
          createChart(
            'Detectivo',
            d.TypeControlDetectiveImplemented,
            d.TypeControlDetectivePartiallyImplemented,
            d.TypeControlDetectiveNotImplemented
          ),
          createChart(
            'Preventivo',
            d.TypeControlPreventiveImplemented,
            d.TypeControlPreventivePartiallyImplemented,
            d.TypeControlPreventiveNotImplemented
          )
        )
      } else if (activeTab.value === 'Conceitos de Cibersegurança') {
        charts.push(
          createChart(
            'Detectar',
            d.CybersecurityConceptDetectImplementado,
            d.CybersecurityConceptDetectParcialmenteImplementado,
            d.CybersecurityConceptDetectNaoImplementado
          ),
          createChart(
            'Identificar',
            d.CybersecurityConceptIdentifyImplementado,
            d.CybersecurityConceptIdentifyParcialmenteImplementado,
            d.CybersecurityConceptIdentifyNaoImplementado
          ),
          createChart(
            'Proteger',
            d.CybersecurityConceptProtectImplementado,
            d.CybersecurityConceptProtectParcialmenteImplementado,
            d.CybersecurityConceptProtectNaoImplementado
          ),
          createChart(
            'Recuperar',
            d.CybersecurityConceptRecoverImplementado,
            d.CybersecurityConceptRecoverParcialmenteImplementado,
            d.CybersecurityConceptRecoverNaoImplementado
          ),
          createChart(
            'Responder',
            d.CybersecurityConceptRespondImplementado,
            d.CybersecurityConceptRespondParcialmenteImplementado,
            d.CybersecurityConceptRespondNaoImplementado
          )
        )
      }
    } else {
      charts.push(...getZeroCharts(activeTab.value))
    }
  } catch (error) {
    console.error('Erro ao carregar gráficos:', error)
    charts.push(...getZeroCharts(activeTab.value))
  } finally {
    isLoading.value = false
  }
}

// Debounce para evitar múltiplos cliques rápidos
const debouncedLoadCharts = debounce((id) => {
  const token = Symbol() // token único para cada requisição
  lastRequestToken = token
  loadCharts(id, token)
}, 250)

function setActiveTab(tab) {
  activeTab.value = tab
  debouncedLoadCharts(props.enterpriseId)
}

watch(
  () => props.enterpriseId,
  (id) => debouncedLoadCharts(id)
)

onMounted(() => debouncedLoadCharts(props.enterpriseId))
</script>

<style scoped>
button:hover {
  cursor: pointer;
}
button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
